import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

const Messages = () => {
  const { messages, users, sendMessage, markMessageAsRead, loading } = useData();
  const { currentUser } = useAuth();
  const [showCompose, setShowCompose] = useState(false);
  const [selectedTab, setSelectedTab] = useState('inbox');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [newMessage, setNewMessage] = useState({
    receiverId: '',
    subject: '',
    content: ''
  });

  // Get current user ID consistently
  const getCurrentUserId = () => {
    return currentUser?.id || currentUser?.uid;
  };

  // Debug logging
  useEffect(() => {
    const userId = getCurrentUserId();
    // console.log('=== MESSAGES DEBUG ===');
    // console.log('Current User:', currentUser);
    // console.log('Current User ID:', userId);
    // console.log('All Messages:', messages);
    // console.log('Users:', users);
  }, [messages, currentUser, users]);

  // Filter messages with proper ID matching
  const currentUserId = getCurrentUserId();
  
  const inboxMessages = messages.filter(message => {
    const isForMe = message.receiverId === currentUserId;
    // console.log(`Message ${message.id}: receiverId=${message.receiverId}, currentUserId=${currentUserId}, match=${isForMe}`);
    return isForMe;
  });
  
  const sentMessages = messages.filter(message => {
    const isFromMe = message.senderId === currentUserId;
    // console.log(`Message ${message.id}: senderId=${message.senderId}, currentUserId=${currentUserId}, match=${isFromMe}`);
    return isFromMe;
  });

  // console.log('Filtered Inbox:', inboxMessages);
  // console.log('Filtered Sent:', sentMessages);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // console.log('Sending message to:', newMessage.receiverId);
      await sendMessage(
        newMessage.receiverId,
        newMessage.content,
        newMessage.subject
      );
      setNewMessage({ receiverId: '', subject: '', content: '' });
      setShowCompose(false);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMessageClick = async (message) => {
    setSelectedMessage(message);
    if (!message.read && message.receiverId === currentUserId) {
      await markMessageAsRead(message.id);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage({
      ...newMessage,
      [e.target.name]: e.target.value
    });
  };

  const currentMessages = selectedTab === 'inbox' ? inboxMessages : sentMessages;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading messages...</p>
      </div>
    );
  }

  return (
    <div className="messages-container">
      <div className="dashboard-header">
        <div className="header-contentt">
          <h2>Messages</h2>
          <p>Send and receive messages with team members</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCompose(true)}
        >
          <i className="fas fa-plus"></i>
          New Message
        </button>
      </div>


      <div className="messages-tabs">
        <button 
          className={`tab ${selectedTab === 'inbox' ? 'active' : ''}`}
          onClick={() => setSelectedTab('inbox')}
        >
          <i className="fas fa-inbox"></i>
          Inbox ({inboxMessages.filter(m => !m.read).length} unread)
        </button>
        <button 
          className={`tab ${selectedTab === 'sent' ? 'active' : ''}`}
          onClick={() => setSelectedTab('sent')}
        >
          <i className="fas fa-paper-plane"></i>
          Sent ({sentMessages.length})
        </button>
      </div>

      <div className="messages-content">
        <div className="messages-list">
          <h3>{selectedTab === 'inbox' ? 'Received Messages' : 'Sent Messages'}</h3>
          {currentMessages.length > 0 ? (
            currentMessages.map(message => (
              <div 
                key={message.id}
                className={`message-item ${!message.read && selectedTab === 'inbox' ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => handleMessageClick(message)}
              >
                <div className="message-header">
                  <div className="message-from">
                    {selectedTab === 'inbox' ? (
                      <strong>From: {message.senderName}</strong>
                    ) : (
                      <strong>To: {message.receiverName}</strong>
                    )}
                  </div>
                  <div className="message-time">
                    {new Date(message.timestamp).toLocaleDateString()}
                  </div>
                </div>
                <div className="message-subject">
                  {message.subject}
                  {!message.read && selectedTab === 'inbox' && (
                    <span className="unread-indicator">●</span>
                  )}
                </div>
                <div className="message-preview">
                  {message.content.substring(0, 100)}
                  {message.content.length > 100 ? '...' : ''}
                </div>
                
                {/* Debug info per message */}
                <div style={{ 
                  fontSize: '10px', 
                  color: '#666', 
                  marginTop: '4px',
                  background: '#f8f9fa',
                  padding: '2px 4px',
                  borderRadius: '3px'
                }}>
                  ID: {message.id} | To: {message.receiverId} | From: {message.senderId}
                </div>
              </div>
            ))
          ) : (
            <div className="no-messages">
              <i className="fas fa-envelope"></i>
              <h3>No Messages</h3>
              <p>
                {selectedTab === 'inbox' 
                  ? 'You have no received messages yet.' 
                  : 'You have not sent any messages yet.'
                }
              </p>
              {selectedTab === 'inbox' && (
                <p style={{fontSize: '14px', color: '#666'}}>
                  Ask someone to send you a message to test the system.
                </p>
              )}
            </div>
          )}
        </div>

        {selectedMessage && (
          <div className="message-detail">
            <div className="message-detail-header">
              <h3>{selectedMessage.subject}</h3>
              <button 
                className="close-detail"
                onClick={() => setSelectedMessage(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="message-detail-meta">
              <div><strong>From:</strong> {selectedMessage.senderName}</div>
              <div><strong>To:</strong> {selectedMessage.receiverName}</div>
              <div><strong>Date:</strong> {new Date(selectedMessage.timestamp).toLocaleString()}</div>
              <div><strong>Status:</strong> {selectedMessage.read ? 'Read' : 'Unread'}</div>
            </div>
            <div className="message-detail-content">
              <p>{selectedMessage.content}</p>
            </div>
          </div>
        )}
      </div>

      {/* COMPOSE MESSAGE MODAL */}
      {showCompose && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Compose New Message</h3>
              <button 
                onClick={() => setShowCompose(false)} 
                className="modal-close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleSendMessage}>
                <div className="form-group">
                  <label className="form-label">To *</label>
                  <select
                    name="receiverId"
                    value={newMessage.receiverId}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">Select recipient...</option>
                    {users.filter(user => user.id !== getCurrentUserId()).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} - {user.role?.replace('_', ' ') || 'No Role'} ({user.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={newMessage.subject}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter message subject..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea
                    name="content"
                    value={newMessage.content}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Type your message here..."
                    rows="6"
                    required
                  />
                </div>

                <div className="modal-actions">
                  <button 
                    type="button" 
                    onClick={() => setShowCompose(false)}
                    className="btn btn-secondary"
                    disabled={submitting}
                  >
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
